import { useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { fmtDay, qty, type ChallanRecord } from "@/types/production";
import { ChallanBadge } from "./JobWorkSection";

const FILTERS = [
  { key: "OPEN", label: "Out with job workers", qs: "status=SENT,PARTIAL" },
  { key: "OVERDUE", label: "Overdue", qs: "overdue=true" },
  { key: "DONE", label: "Received", qs: "status=RECEIVED" },
  { key: "ALL", label: "All", qs: "" },
];

/** Every job-work challan, to see what is out with whom and what is late */
export function JobWorkList({ reloadKey }: { reloadKey: number }) {
  const { error } = useToast();
  const [search, setSearch] = useUrlSearch();
  const [filter, setFilter] = useState("OPEN");
  const list = usePagedList<ChallanRecord>({
    deps: [search, filter, reloadKey],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams(FILTERS.find((x) => x.key === filter)!.qs);
      qs.set("page", String(page));
      qs.set("page_size", "30");
      if (search.trim()) qs.set("search", search.trim());
      try {
        return unwrapPage<ChallanRecord>(await api.get(`/api/ProductionStock/Challans?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load challans");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Search by challan, job worker or work order…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}>{f.label}</button>
          ))}
        </div>
      </div>
      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={5} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Challan</TableHead><TableHead>Job worker</TableHead><TableHead>Work order</TableHead><TableHead>Sent / back</TableHead><TableHead>Due back</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((c) => (
                <TableRow key={c.id} className="hover:bg-muted/40">
                  <TableCell className="font-mono text-sm font-semibold">{c.challan_number}<div className="text-[11px] font-sans font-normal text-muted-foreground">{fmtDay(c.challan_date)}</div></TableCell>
                  <TableCell>{c.supplier?.name}</TableCell>
                  <TableCell>{c.work_order ? <Link to={`/production/${c.work_order.id}`} className="text-primary hover:underline font-mono text-sm">{c.work_order.wo_number}</Link> : "-"}<div className="text-[11px] text-muted-foreground">{c.work_order?.product_name}</div></TableCell>
                  <TableCell className="text-sm">{qty(c.sent_qty, c.unit)} <span className="text-muted-foreground">/ {qty(c.received_qty)}</span></TableCell>
                  <TableCell className={c.overdue ? "text-rose-600 font-semibold" : ""}>{fmtDay(c.expected_return)}</TableCell>
                  <TableCell><ChallanBadge status={c.challan_status} overdue={c.overdue} /></TableCell>
                </TableRow>
              ))}
              {!list.rows.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No challans here</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
    </div>
  );
}
